import axios from "axios";
import { api } from "./api_client";
import { Airport } from "@/models/airport";
import { localDateIso } from "@/daily/game";
import { language } from "@/daily/strings";
import { bearing, interpolateGreatCircle } from "@/daily/geo";
import { ClueType, DailyClue, DailyPuzzle, LatLon } from "@/daily/types";
import { transformAirport } from "@/utils/game_transforms";

// Points drawn along the flown part of the route.
const TRACK_STEPS = 12;

interface ApiClue {
  type: ClueType;
  short_fact: string;
  title: string;
  detail: string;
}

const transformClue = (clue: ApiClue): DailyClue => ({
  type: clue.type,
  shortFact: clue.short_fact,
  title: clue.title,
  detail: clue.detail,
});

// The puzzle for the player's own date, from GET /daily. The API gives the two airports and how far
// along the route the plane is; until real tracks exist, the flown path is
// drawn as a great-circle line up to that point.
//
// /daily?draft=NAME in the browser previews a draft puzzle before it is
// published.
export const requestedDraft = (): string | undefined =>
  new URLSearchParams(window.location.search).get("draft") || undefined;

// /daily?preview=DATE&key=KEY shows the editor a built puzzle before its
// date.
const requestedPreview = (): { preview?: string; key?: string } => {
  const query = new URLSearchParams(window.location.search);
  const preview = query.get("preview") || undefined;
  return preview ? { preview, key: query.get("key") || undefined } : {};
};

const API_URL = import.meta.env.VITE_API_BASE_URL;

// True when the address asks for a draft or an editor's preview, which are
// always fetched fresh and never remembered.
export const isPreview = (): boolean =>
  !!requestedDraft() || !!requestedPreview().preview;

// The puzzle as the API sends it. It needs no session, so it is asked for
// with a plain request: nothing has to be set up first.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchPuzzleData = async (): Promise<any> => {
  const { preview, key } = requestedPreview();
  const { data } = await axios.get(`${API_URL}/daily`, {
    // The clues come back in the player's language where the API has them.
    params: { date: localDateIso(), lang: language(), draft: requestedDraft(), preview, key },
  });
  return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parsePuzzle = (data: any): DailyPuzzle => {
  const { preview } = requestedPreview();
  // Either kind of preview plays as a draft: fresh each time it is opened.
  const draft = requestedDraft() ?? (preview && `preview-${preview}`);

  const origin = transformAirport(data.origin);
  const destination = transformAirport(data.destination);
  // Puzzles built from a real flight carry the path it flew; hand-written
  // ones do not, and get a straight route up to the cutoff.
  const flown: [number, number][] | null = data.track;
  const track: LatLon[] = flown
    ? flown.map(([lat, lon]) => ({ lat, lon }))
    : Array.from({ length: TRACK_STEPS + 1 }, (_, index) =>
        interpolateGreatCircle(origin, destination, (index / TRACK_STEPS) * data.cutoff)
      );
  const current = track[track.length - 1];
  const previous = track[track.length - 2];

  return {
    date: data.date,
    draft,
    origin,
    destination,
    aircraftMaker: data.aircraft_maker,
    aircraftModel: data.aircraft_model,
    departureTimeLocal: data.departure_time_local,
    airline: data.airline,
    flightNumber: data.flight_number,
    durationMinutes: data.duration_minutes,
    track,
    position: { ...current, direction: bearing(previous, current) },
    clues: data.clues.map(transformClue),
  };
};

// Every airport a player can guess, as the API sends it: those with at
// least one flight a day, busiest first. Falls back to the classic game's
// full list if the API does not have the daily list yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchAirportData = async (): Promise<any[]> => {
  try {
    return (await axios.get(`${API_URL}/daily/airports`)).data;
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 404) throw error;
    return (await api.get("/airports?difficulty=hard&region=world")).data;
  }
};

// Airports without a city or code cannot be searched for or shown, and ones
// with no daily flights are not offered, so both are left out.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseAirports = (data: any[]): Airport[] =>
  data
    .map(transformAirport)
    .filter(
      (airport) =>
        airport.name && airport.city && airport.iata && airport.daily_departures > 0
    );

// ---------- The editor's page ----------

// How a local-knowledge candidate did when it was tested blind: roughly how
// many players in a hundred it would lead to the destination, whether it
// points to the city or only the country, and the place it was taken for.
export interface CandidateQuality {
  score: number;
  pointsTo: "city" | "country" | "elsewhere" | "letter";
  guess: string;
}

export interface LocalKnowledgeOption extends DailyClue {
  // Missing on candidates built before they were tested.
  quality: CandidateQuality | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformOption = (option: any): LocalKnowledgeOption => ({
  ...transformClue(option),
  quality: option.quality
    ? {
        score: option.quality.score,
        pointsTo: option.quality.points_to,
        guess: option.quality.guess,
      }
    : null,
});

export interface UpcomingPuzzle {
  date: string;
  locksAt: string;
  locked: boolean;
  origin: Airport;
  destination: Airport;
  airline: string;
  flightNumber: string;
  aircraft: string;
  clues: DailyClue[];
  localKnowledgeOptions: LocalKnowledgeOption[];
  localKnowledgeChoice: number | null;
  notes: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformUpcoming = (data: any): UpcomingPuzzle => ({
  date: data.date,
  locksAt: data.locks_at,
  locked: data.locked,
  origin: transformAirport(data.origin),
  destination: transformAirport(data.destination),
  airline: data.airline,
  flightNumber: data.flight_number,
  aircraft: data.aircraft,
  clues: data.clues.map(transformClue),
  localKnowledgeOptions: data.local_knowledge_options.map(transformOption),
  localKnowledgeChoice: data.local_knowledge_choice,
  notes: data.notes,
});

export const fetchUpcomingPuzzles = async (key: string): Promise<UpcomingPuzzle[]> => {
  const { data } = await api.get("/daily/admin/puzzles", { params: { key } });
  return data.map(transformUpcoming);
};

// Asks for one puzzle's clues to be written again, keeping its flight. The
// work is done in the background and takes a minute or two.
export const rewriteClues = async (key: string, date: string): Promise<void> => {
  await api.post(`/daily/admin/puzzles/${date}/rewrite`, { key });
};

export const chooseLocalKnowledge = async (
  key: string,
  date: string,
  choice: number
): Promise<UpcomingPuzzle> => {
  const { data } = await api.post(`/daily/admin/puzzles/${date}/choice`, { key, choice });
  return transformUpcoming(data);
};
